import { AppContext, BookingData, Organisation, TimeSlot } from "../../../AppDataProvider/AppDataProvider";
import { dateTimeFormats, paddingBottom, timeFormats } from "../../../common/styles";
import { LoadingSpinner, SelectableButton } from "../../../common/components";
import React, { useContext } from "react";
import styled, { useTheme } from "styled-components";
import { PartRequired } from "../../../utils/types";
import { requireBookingDataFields } from "../../../utils";
import { useTranslation } from "react-i18next";
export type TimeSlotSelectionProps = {
  createReservationLoading: boolean;
  /**
   * Callback function which is called when a time slot is selected.
   */
  onSelectTimeSlot: (TimeSlot: TimeSlot) => void;
  organisation: Organisation;
  queryByOrganisationTimeZone?: boolean;
  selectedOrganisation?: Organisation | null;
  /**
   * The currently selected timeslot (optional)
   */
  selectedTimeSlot?: TimeSlot | null;
  timeSlots: TimeSlot[];
};
type LocationAvailabilityStepBookingData = PartRequired<BookingData, "timezone">;
export function TimeSlotSelection(props: TimeSlotSelectionProps): React.ReactElement {
  // Note - not supporting recurring/group bookings here but can be revisited
  const {
    i18n
  } = useTranslation();
  const theme = useTheme();
  const {
    createReservationLoading,
    onSelectTimeSlot,
    organisation,
    queryByOrganisationTimeZone,
    selectedOrganisation,
    selectedTimeSlot,
    timeSlots
  } = props;
  const {
    appData
  } = useContext(AppContext);
  type TimeSlotSelectionBookingData = PartRequired<BookingData, "timezone">;
  requireBookingDataFields<LocationAvailabilityStepBookingData>(appData.bookingData, ["timezone"]);
  const {
    timezone
  } = appData.bookingData as TimeSlotSelectionBookingData;
  const selectTimeSlot = (timeSlot: TimeSlot) => {
    onSelectTimeSlot(timeSlot);
  };
  if (timeSlots.length === 0) return <Container />;
  return <Container data-sentry-element="Container" data-sentry-component="TimeSlotSelection" data-sentry-source-file="TimeSlotSelection.tsx">
      <TimeSlotsContainer data-sentry-element="TimeSlotsContainer" data-sentry-source-file="TimeSlotSelection.tsx">
        {timeSlots.map(timeSlot => {
        const {
          interval
        } = timeSlot;
        const {
          start
        } = interval;
        const startDate = new Date(start);
        const timezoneFormat = queryByOrganisationTimeZone && organisation.defaultTimeZone ? organisation.defaultTimeZone : timezone;
        const displayStartTime = startDate.toLocaleString(i18n.language, {
          ...timeFormats.default,
          timeZone: timezoneFormat
        });
        const buttonAriaLabel = startDate.toLocaleString(i18n.language, {
          ...dateTimeFormats.full,
          timeZone: timezoneFormat
        });
        const isSelected = selectedTimeSlot?.interval.start === start && selectedOrganisation?._id === organisation._id;
        const showLoadingSpinner = createReservationLoading && isSelected;
        return <SelectableButton key={`${organisation._id}-${start}`} selected={isSelected} aria-label={buttonAriaLabel} onClick={() => {
          selectTimeSlot(timeSlot);
        }}>
              {showLoadingSpinner ? <LoadingSpinner colour={theme.colours.textOnPrimary} /> : displayStartTime}
            </SelectableButton>;
      })}
      </TimeSlotsContainer>
    </Container>;
}
const Container = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  ${paddingBottom.m}
`;
const TimeSlotsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(6rem, 100%/3), 1fr));
  grid-auto-rows: 2.5rem;
  grid-gap: 0.25rem;
`;